
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, LockIcon, SparklesIcon } from './icons';
import { useTranslation } from '../i18n/useTranslation';

interface PremiumFeatureModalProps {
    isOpen: boolean;
    onClose: () => void;
    requiredLevel?: string;
}

const PremiumFeatureModal: React.FC<PremiumFeatureModalProps> = ({ isOpen, onClose, requiredLevel = 'Premium' }) => {
    const { t } = useTranslation();

    const handleRedirect = () => {
        // In this app, /services seems to be the place for memberships 
        // based on the nav bar having 'Services' and portfolioPage having membership plans.
        window.location.href = '/services';
        onClose();
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
                    aria-modal="true"
                    role="dialog"
                >
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.9, opacity: 0, y: 20 }}
                        onClick={(e) => e.stopPropagation()}
                        className="relative bg-white dark:bg-gray-800 rounded-3xl w-full max-w-md flex flex-col shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                    >
                        {/* Header / Accent */}
                        <div className="h-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />

                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 p-2 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            aria-label={t('common_close')}
                        >
                            <XIcon className="w-5 h-5" />
                        </button>

                        <div className="p-8 text-center">
                            <div className="mb-6 relative inline-block">
                                <div className="w-20 h-20 bg-indigo-100 dark:bg-indigo-900/40 rounded-2xl flex items-center justify-center mx-auto">
                                    <LockIcon className="w-10 h-10 text-indigo-600 dark:text-indigo-400" />
                                </div>
                                <div className="absolute -top-2 -right-2 w-8 h-8 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center shadow-lg">
                                    <SparklesIcon className="w-5 h-5 text-amber-400" />
                                </div>
                            </div>

                            <h2 className="text-2xl font-serif tracking-tight text-gray-900 dark:text-white mb-3">
                                {t('premium_feature_modal_title')}
                            </h2>

                            <p className="text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
                                {t('premium_feature_modal_desc', { level: requiredLevel })}
                            </p>

                            <div className="space-y-3">
                                <button
                                    onClick={handleRedirect}
                                    className="w-full bg-indigo-600 text-white font-bold py-4 px-6 rounded-2xl hover:bg-indigo-700 transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-indigo-200 dark:shadow-none"
                                >
                                    {t('premium_feature_modal_cta')}
                                </button>

                                <button
                                    onClick={onClose}
                                    className="w-full text-gray-500 font-semibold py-3 px-6 rounded-2xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                    {t('common_cancel')}
                                </button>
                            </div>
                        </div>

                        {/* Footer decoration */}
                        <div className="p-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-700">
                            <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">
                                Constantina AI Styling Platform
                            </p>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default PremiumFeatureModal;
